/**
 * Subscription Context
 *
 * Provides subscription state to React components.
 * Fetches user + subscription data from /bff/subscription endpoint.
 *
 * Key Design:
 * - DB is source of truth (backend always reads from DB)
 * - Context caches subscription to avoid repeated API calls
 * - Provides refetch() function for when subscription changes (e.g., after checkout)
 * - Exposes loading and error states for UI feedback
 *
 * Usage:
 * 1. Wrap app/dashboard with SubscriptionProvider
 * 2. Use useSubscription() hook in components
 * 3. Call refetch() after subscription changes
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const SubscriptionContext = createContext(null);

/**
 * Routes that require subscription data fetch
 * Only fetch subscription data on dashboard/protected routes to avoid 401 errors on public pages
 */
const PROTECTED_ROUTE_PREFIXES = ['/dashboard', '/get-access', '/welcome'];

/**
 * Subscription Provider Component
 *
 * Fetches subscription data on mount and provides it to children.
 * Only fetches on protected routes (dashboard, get-access) to avoid 401 errors on public pages.
 * Automatically retries on auth errors (redirects to login).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.requireAuth - If true, redirect to login when not authenticated
 */
export function SubscriptionProvider({ children, requireAuth = false }) {
  const location = useLocation();

  // Determine if we're on a route that needs subscription data
  const isProtectedRoute = PROTECTED_ROUTE_PREFIXES.some(prefix =>
    location.pathname.startsWith(prefix)
  );

  const [state, setState] = useState({
    loading: isProtectedRoute, // Only loading if we're on a protected route
    error: null,
    authenticated: false,
    user: null,
    subscription: null,
    // True once /bff/subscription has answered. Public pages only fetch
    // /auth/status, which cannot tell us about a subscription.
    subscriptionLoaded: false,
  });

  /**
   * Fetch subscription data from backend
   *
   * Calls /bff/subscription which:
   * 1. Checks session authentication
   * 2. Creates/updates user in database
   * 3. Reads subscription from database (source of truth)
   * 4. Caches subscription in session (performance)
   * 5. Returns { authenticated, user, subscription }
   */
  const fetchSubscription = useCallback(async () => {
    // Enhanced logging with timestamp
    const timestamp = new Date().toISOString();
    console.log(`%c[SubscriptionContext ${timestamp}] 🔄 Fetching subscription`, 'color: blue; font-weight: bold');
    console.log(`[SubscriptionContext] Current URL: ${window.location.href}`);
    console.log(`[SubscriptionContext] Backend URL: ${API_BASE_URL}/bff/subscription`);

    try {
      const response = await fetch(`${API_BASE_URL}/bff/subscription`, {
        method: 'GET',
        credentials: 'include', // CRITICAL: Send session cookie
      });

      console.log(`[SubscriptionContext] Response status: ${response.status}`);
      console.log('[SubscriptionContext] Response headers:', {
        'content-type': response.headers.get('content-type'),
        'set-cookie': response.headers.get('set-cookie'),
      });

      // Handle authentication errors
      if (response.status === 401) {
        console.warn('%c[SubscriptionContext] ❌ 401 Unauthorized - Session invalid', 'color: red; font-weight: bold');
        console.log('[SubscriptionContext] requireAuth:', requireAuth);

        setState({
          loading: false,
          error: null,
          authenticated: false,
          user: null,
          subscription: null,
          subscriptionLoaded: true,
        });

        // Redirect to login if required
        if (requireAuth) {
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          console.log(`[SubscriptionContext] Redirecting to login with returnUrl: ${returnUrl}`);
          window.location.href = `${API_BASE_URL}/auth/login?returnUrl=${returnUrl}`;
        }

        return;
      }

      // Handle other errors
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[SubscriptionContext] HTTP Error ${response.status}:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      console.log('%c[SubscriptionContext] ✅ Auth data received', 'color: green; font-weight: bold');
      console.log('[SubscriptionContext] Data:', {
        authenticated: data.authenticated,
        userId: data.user?.id,
        email: data.user?.email,
        displayName: data.user?.name,
        subscriptionStatus: data.subscription?.status,
        planCode: data.subscription?.planCode,
        source: data.subscription?.source,
      });

      setState({
        loading: false,
        error: null,
        authenticated: data.authenticated,
        user: data.user,
        subscription: data.subscription,
        subscriptionLoaded: true,
      });
    } catch (error) {
      console.error('%c[SubscriptionContext] ❌ Fetch error', 'color: red; font-weight: bold');
      console.error('[SubscriptionContext] Error message:', error.message);
      console.error('[SubscriptionContext] Error stack:', error.stack);

      setState({
        loading: false,
        error: error.message,
        authenticated: false,
        user: null,
        subscription: null,
        subscriptionLoaded: true,
      });
    }
  }, [requireAuth]);

  /**
   * Lightweight sign-in check for public pages.
   *
   * /bff/subscription 401s for ANONYMOUS visitors, which is why it was gated to
   * protected routes. It does not 401 for a signed-in one. So: ask /auth/status
   * first (200 either way, no database, no Stripe), and only load the
   * subscription when someone is actually signed in.
   *
   * Loading it here rather than on arrival at the dashboard matters — otherwise
   * clicking Account paints the dashboard shell while the subscription is still
   * in flight, which reads as a signed-in user briefly having no plan.
   */
  const fetchAuthStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/status`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();

      // Identity comes from /auth/status and nothing else. Set it first, so a
      // later subscription failure can't blank it out and show "Sign In" to
      // someone who is demonstrably signed in.
      setState((prev) => ({
        ...prev,
        loading: false,
        error: null,
        authenticated: Boolean(data.authenticated),
        user: data.user || null,
        subscription: data.authenticated ? prev.subscription : null,
        subscriptionLoaded: data.authenticated ? prev.subscriptionLoaded : false,
      }));

      if (data.authenticated) {
        // Enrich with the subscription so the dashboard can render immediately
        // when they navigate to it. Failure here must not affect sign-in state.
        try {
          await fetchSubscription();
        } catch (subscriptionError) {
          console.warn('[SubscriptionContext] Subscription enrich failed:', subscriptionError);
        }
      }
    } catch (error) {
      // /auth/status itself is unreachable — a public page must still render.
      setState({
        loading: false,
        error: null,
        authenticated: false,
        user: null,
        subscription: null,
        subscriptionLoaded: false,
      });
    }
  }, [fetchSubscription]);

  // Protected routes need the full subscription; public routes only need to
  // know whether someone is signed in.
  useEffect(() => {
    if (isProtectedRoute) {
      fetchSubscription();
    } else {
      fetchAuthStatus();
    }
  }, [isProtectedRoute, fetchSubscription, fetchAuthStatus]);

  /**
   * Refetch subscription data
   *
   * Call this after subscription changes (e.g., after checkout success)
   * to refresh the cached subscription state.
   */
  const refetch = useCallback(() => {
    console.log('[SubscriptionContext] Refetching subscription...');
    setState((prev) => ({ ...prev, loading: true }));
    fetchSubscription();
  }, [fetchSubscription]);

  /*
   * A protected route has nothing to decide with until /bff/subscription has
   * answered. Public routes only load /auth/status, so arriving from one leaves
   * subscription null — and ProtectedRoute would read that as "no active plan"
   * and bounce to /get-access, which then finds the real subscription and
   * forwards to /welcome. That was the flash.
   *
   * This is derived during render on purpose: child effects run before parent
   * effects, so flipping a loading flag from the provider's own effect happens
   * after ProtectedRoute has already redirected.
   */
  const awaitingSubscription = isProtectedRoute && !state.subscriptionLoaded;

  // Context value
  const value = {
    loading: state.loading || awaitingSubscription,
    error: state.error,
    authenticated: state.authenticated,
    user: state.user,
    subscription: state.subscription,
    refetch,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * useSubscription Hook
 *
 * Access subscription state from any component within SubscriptionProvider.
 *
 * Returns:
 * - loading: boolean - True while fetching subscription data
 * - error: string | null - Error message if fetch failed
 * - authenticated: boolean - True if user is authenticated
 * - user: Object | null - User object { id, email, name }
 * - subscription: Object | null - Subscription object { status, planCode, source, ... }
 * - refetch: function - Call to refetch subscription data
 *
 * Example:
 * ```js
 * const { loading, subscription, refetch } = useSubscription();
 *
 * if (loading) return <Spinner />;
 * if (subscription.status === 'active') {
 *   // User has active subscription
 * } else if (subscription.status === 'none') {
 *   // User has no subscription
 * }
 * ```
 */
export function useSubscription() {
  const context = useContext(SubscriptionContext);

  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }

  return context;
}

export default SubscriptionContext;
